import { shouldDeliverCableMessage } from '../channelMessageDeduper.js';

const subs = new Map();

const normalizePayload = (payload) => {
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
};

const unwrapActionCableEnvelope = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (!payload.identifier || payload.message == null) return payload;
  return normalizePayload(payload.message);
};

const isMlsTraceEnabled = () => {
  try {
    const win = typeof window !== 'undefined' ? window : null;
    const forced = win?.__MLS_TRACE__ === true || win?.__MLS_DEBUG__ === true;
    if (forced) return true;
    const host = win?.location?.hostname || '';
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  } catch {
    return false;
  }
};

const appendMlsTraceEvent = (scope, event) => {
  try {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(window.__MLS_TRACE_EVENTS__)) window.__MLS_TRACE_EVENTS__ = [];
    const list = window.__MLS_TRACE_EVENTS__;
    list.push({ scope, ...event });
    if (list.length > 500) list.splice(0, list.length - 500);
  } catch {}
};

const traceCablePayload = (stage, payload = {}) => {
  if (!isMlsTraceEnabled()) return;
  try {
    const event = {
      stage,
      at: 'mlsGroup.js',
      ts: new Date().toISOString(),
      ...payload,
    };
    appendMlsTraceEvent('MlsGroupChannel', event);
    console.log('[MLS TRACE][MlsGroupChannel]', event);
  } catch {}
};

const releaseGroupSubscription = (groupId) => {
  const key = String(groupId);
  const existing = subs.get(key);
  if (existing?.subscription?.unsubscribe) {
    try {
      existing.subscription.unsubscribe();
    } catch {}
  }
  subs.delete(key);
};

const mlsGroupChannel = (cable, groupId, handlers = {}) => {
  if (!cable || typeof cable?.subscriptions?.create !== 'function') return null;
  if (!groupId) return null;

  const key = String(groupId);
  const nextHandlers = {
    onConnected: handlers.onConnected || null,
    onDisconnected: handlers.onDisconnected || null,
    onReceive: handlers.onReceive || null,
  };

  const existing = subs.get(key);
  if (existing) {
    if (existing.cable !== cable) {
      releaseGroupSubscription(key);
    } else {
      existing.handlers.onConnected = nextHandlers.onConnected;
      existing.handlers.onDisconnected = nextHandlers.onDisconnected;
      existing.handlers.onReceive = nextHandlers.onReceive;
      return existing.subscription;
    }
  }

  try {
    const handlerRef = { ...nextHandlers };
    const subscription = cable.subscriptions.create(
      { channel: 'MlsGroupChannel', group_id: groupId },
      {
        connected: () => handlerRef.onConnected?.(),
        disconnected: () => handlerRef.onDisconnected?.(),
        received: async (payload) => {
          const data = unwrapActionCableEnvelope(normalizePayload(payload));
          if (data) {
            if (!shouldDeliverCableMessage(`mls-group:${key}`, data)) return;
            traceCablePayload('cable:received', {
              hasHistoryCiphertext: Boolean(
                data?.history_ciphertext_b64 || data?.historyCiphertextB64 || null,
              ),
              hasHistoryIv: Boolean(data?.history_iv_b64 || data?.historyIvB64 || null),
              serverMsgId: data?.server_msg_id ?? null,
              serverSeq: data?.server_seq ?? null,
              msgType: data?.msg_type ?? data?.type ?? null,
              keys:
                data && typeof data === 'object'
                  ? Object.keys(data).slice(0, 40)
                  : [],
            });
            // Tag cable origin and a message id for traceability
            try {
              if (typeof data === 'object') {
                data.__origin = 'cable:mls_group';
                data.__cableMsgId = `cable-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
              }
            } catch {}

            handlerRef.onReceive?.(data);
          }
        },
      },
    );

    subs.set(key, { subscription, handlers: handlerRef, cable });
    return subscription;
  } catch (error) {
    console.warn('MLS group channel subscription failed', error);
    return null;
  }
};

export { releaseGroupSubscription };
export default mlsGroupChannel;
